import { formatDurationForDisplay, formattedDate, HStack, Text } from '@talos/kyoko';
import styled from 'styled-components';

const TimeText = styled(Text)<{ $muted?: boolean }>`
  font-size: ${({ theme }) => theme.fontSizeSmall}rem;
  color: ${({ theme, $muted }) => theme[$muted ? 'colorTextSubtle' : 'colorTextImportant']};
`;

interface TimeValueProps {
  duration?: string;
  startTime?: string;
  endTime?: string;
}

/**
 * Adaptive time display component.
 * - If Duration exists: shows "1h (start time)"
 * - If both StartTime and EndTime: shows "start - end"
 * - If only StartTime or EndTime: shows that time
 * - If none: shows "-"
 */
export function TimeValue({ duration, startTime, endTime }: TimeValueProps) {
  // Duration with optional start time: "1h (start)"
  if (duration) {
    // Use shared Kyoko formatting so duration rendering stays consistent across app surfaces.
    const displayDuration = formatDurationForDisplay(duration) ?? duration;
    const suffix = startTime ? `(${formattedDate(startTime)})` : undefined;
    return (
      <HStack gap="spacing4" alignItems="center">
        <TimeText>{displayDuration}</TimeText>
        {suffix && <TimeText $muted>{suffix}</TimeText>}
      </HStack>
    );
  }

  // Both times: "start - end"
  if (startTime && endTime) {
    return (
      <TimeText>
        {formattedDate(startTime)} - {formattedDate(endTime)}
      </TimeText>
    );
  }

  // Single time (start or end)
  if (startTime) {
    return <TimeText>{formattedDate(startTime)}</TimeText>;
  }
  if (endTime) {
    return <TimeText>{formattedDate(endTime)}</TimeText>;
  }

  // No time data
  return <TimeText>-</TimeText>;
}
